// src/utils/imageUtils.js

/**
 * Get full image URL from relative path or return absolute URL as-is
 * @param {string} imagePath - The image path (relative or absolute)
 * @returns {string|null} - Full image URL or null if no path provided
 */
export const getImageUrl = (imagePath) => {
  // Return null if no image path provided
  if (!imagePath) return null;
  
  // If image path is already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Get image base URL from environment variable with fallbacks
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 
                      process.env.REACT_APP_API_URL?.replace('/api', '') || 
                      'http://localhost:5000';
  
  // Ensure imagePath starts with /
  const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  
  return `${imageBaseUrl}${cleanPath}`;
};

/**
 * Get image URL with error handling for React img onError
 * @param {string} imagePath - The image path
 * @param {string} fallbackPath - Optional fallback image path
 * @returns {string|null} - Full image URL or fallback URL
 */
export const getImageUrlWithFallback = (imagePath, fallbackPath = null) => {
  const primaryUrl = getImageUrl(imagePath);
  if (primaryUrl) return primaryUrl;
  
  return fallbackPath ? getImageUrl(fallbackPath) : null;
};

/**
 * Handle image load error by showing placeholder or fallback
 * @param {Event} event - The error event from img element
 * @param {string} fallbackSrc - Optional fallback image source
 */
export const handleImageError = (event, fallbackSrc = null) => {
  const img = event.target;
  
  if (fallbackSrc && img.src !== getImageUrl(fallbackSrc)) {
    // Try fallback image first
    img.src = getImageUrl(fallbackSrc);
  } else {
    // Hide image and show placeholder
    img.style.display = 'none';
    if (img.nextSibling) {
      img.nextSibling.style.display = 'flex';
    }
  }
};

/**
 * Check if image URL is valid and accessible
 * @param {string} imagePath - The image path to check
 * @returns {Promise<boolean>} - Promise that resolves to true if image is valid
 */
export const isImageValid = (imagePath) => {
  return new Promise((resolve) => {
    if (!imagePath) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = getImageUrl(imagePath);
  });
};

/**
 * Preload images for better user experience
 * @param {string[]} imagePaths - Array of image paths to preload
 * @returns {Promise<string[]>} - Promise that resolves to array of successfully loaded image URLs
 */
export const preloadImages = async (imagePaths) => {
  const loadPromises = imagePaths.map(path => 
    isImageValid(path).then(isValid => isValid ? getImageUrl(path) : null)
  );
  
  const results = await Promise.all(loadPromises);
  return results.filter(url => url !== null);
};

const pickImage = (images) => {
  if (!Array.isArray(images) || images.length === 0) return null;
  return (images.find(img => img.isMain) || images[0])?.path || null;
};

/**
 * Resolve a procurement line item's thumbnail (variation image first, then product image)
 * @param {Object} item - Line item carrying Product / ProductVariation with `images`
 * @returns {string|null} - Full image URL or null
 */
export const getProcurementItemImage = (item) => {
  if (!item) return null;
  const path = pickImage(item.ProductVariation?.images) || pickImage(item.Product?.images);
  return path ? getImageUrl(path) : null;
};
